<script lang="ts">
  /**
   * Manage — the two jobs that kept sending you back to a terminal.
   *
   *   Scheduler   create the missing jobs, run one now, pause or resume
   *   Labels      bulk-apply `portal=` labels for cost attribution
   *
   * Both are administrative rather than destructive, which is why they get
   * buttons. Nothing here deletes a job, a service or a project — removal stays a
   * deliberate act at the console, the same line `Estate.svelte` draws for portal
   * on/off.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type Job = {
    id: string;
    schedule: string;
    timeZone: string;
    state: string;
    uri: string | null;
    lastAttemptTime: string | null;
    lastOk: boolean;
    foreign: boolean;
  };
  type Missing = { id: string; schedule: string; why: string; uri: string };
  type Unlabelled = { id: string; project: string; region: string; suggested: string | null };

  type Panel<T> = { data: T | null; error: string | null; loading: boolean };

  const sched = $state<
    Panel<{
      location: string;
      reachable: boolean;
      jobs: Job[];
      missing: Missing[];
      schedulerServiceAccount: string | null;
      note?: string;
    }>
  >({ data: null, error: null, loading: true });

  const labels = $state<Panel<{ services: Unlabelled[]; unreadable: string[]; portalIds: string[] }>>({
    data: null, error: null, loading: true,
  });

  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  /** Chosen portal per service, keyed `project/id`. Starts from the suggestion. */
  let choice = $state<Record<string, string>>({});

  async function fill<T>(p: Panel<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  async function loadLabels() {
    await fill(labels, "/admin/services/unlabelled");
    const next: Record<string, string> = {};
    for (const s of labels.data?.services ?? []) next[`${s.project}/${s.id}`] = s.suggested ?? "";
    choice = next;
  }

  $effect(() => {
    void fill(sched, "/admin/scheduler");
    void loadLabels();
  });

  const chosenCount = $derived(Object.values(choice).filter(Boolean).length);

  async function ensureJobs() {
    busy = "ensure";
    try {
      const ok = await stepUp.guard(async () => {
        const r = await api.post<{ created: string[]; failed: { id: string; error: string }[] }>("/admin/scheduler/ensure");
        toast =
          r.failed.length > 0
            ? { kind: "warn", text: `Created ${r.created.length}; ${r.failed.length} failed — ${r.failed[0]?.error ?? ""}` }
            : { kind: "ok", text: r.created.length === 0 ? "Nothing missing." : `Created ${r.created.join(", ")}.` };
      }, "Creating the missing scheduler jobs");
      if (!ok) toast = { kind: "warn", text: "Cancelled." };
      await fill(sched, "/admin/scheduler");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not create the jobs" };
    } finally {
      busy = null;
    }
  }

  async function runJob(j: Job) {
    busy = j.id;
    try {
      const ok = await stepUp.guard(() => api.post(`/admin/scheduler/${j.id}/run`), `Running ${j.id} now`);
      toast = ok ? { kind: "ok", text: `${j.id} triggered.` } : { kind: "warn", text: "Cancelled." };
      if (ok) await fill(sched, "/admin/scheduler");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not run it" };
    } finally {
      busy = null;
    }
  }

  async function togglePause(j: Job) {
    const paused = j.state !== "PAUSED";
    busy = j.id;
    try {
      const ok = await stepUp.guard(
        () => api.patch(`/admin/scheduler/${j.id}`, { paused }),
        `${paused ? "Pausing" : "Resuming"} ${j.id}`,
      );
      toast = ok ? { kind: "ok", text: `${j.id} ${paused ? "paused" : "resumed"}.` } : { kind: "warn", text: "Cancelled." };
      if (ok) await fill(sched, "/admin/scheduler");
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not change it" };
    } finally {
      busy = null;
    }
  }

  async function applyLabels() {
    const payload = (labels.data?.services ?? [])
      .map((s) => ({ ...s, portal: choice[`${s.project}/${s.id}`] ?? "" }))
      .filter((s) => s.portal)
      .map((s) => ({ project: s.project, region: s.region, id: s.id, portal: s.portal }));
    if (payload.length === 0) return;

    busy = "labels";
    try {
      const ok = await stepUp.guard(async () => {
        const r = await api.post<{ applied: string[]; failed: { id: string; error: string }[] }>(
          "/admin/services/label",
          { labels: payload.slice(0, 100) },
        );
        toast =
          r.failed.length > 0
            ? { kind: "warn", text: `Labelled ${r.applied.length}; ${r.failed.length} failed — ${r.failed[0]?.error ?? ""}` }
            : { kind: "ok", text: `Labelled ${r.applied.length} services. Cost can now be attributed.` };
      }, `Labelling ${Math.min(payload.length, 100)} services`);
      if (!ok) toast = { kind: "warn", text: "Cancelled." };
      await loadLabels();
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not apply labels" };
    } finally {
      busy = null;
    }
  }

  /** Apply one portal id to every row in a project — the 48-service case. */
  function fillProject(project: string, portal: string) {
    const next = { ...choice };
    for (const s of labels.data?.services ?? []) if (s.project === project) next[`${s.project}/${s.id}`] = portal;
    choice = next;
  }

  const projects = $derived([...new Set((labels.data?.services ?? []).map((s) => s.project))].sort());
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  <!-- ── Scheduler ─────────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Scheduled jobs</span>
      <span class="acts">
        <span class="sub">{sched.data?.location ?? ""}</span>
        {#if (sched.data?.missing.length ?? 0) > 0}
          <button class="btn-primary" disabled={busy === "ensure"} onclick={ensureJobs}>
            {busy === "ensure" ? "Creating…" : `Create ${sched.data!.missing.length} missing`}
          </button>
        {/if}
      </span>
    </div>

    {#if sched.loading}
      <p class="sub pad">Loading…</p>
    {:else if sched.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {sched.error}</p>
    {:else if sched.data}
      {#if sched.data.note}
        <p class="sub pad"><Icon name="alert" size={12} /> {sched.data.note}</p>
      {/if}

      {#each sched.data.missing as m (m.id)}
        <div class="row row--warn">
          <Icon name="alert" size={14} />
          <div class="grow">
            <div class="row__t">{m.id} <span class="sub num">{m.schedule}</span></div>
            <div class="sub">{m.why}</div>
            <div class="sub num">{m.uri}</div>
          </div>
        </div>
      {/each}

      {#if sched.data.jobs.length > 0}
        <div class="scroll">
          <table>
            <thead><tr><th>Job</th><th>Schedule</th><th>State</th><th>Last run</th><th></th></tr></thead>
            <tbody>
              {#each sched.data.jobs as j (j.id)}
                <tr>
                  <td>
                    <div class="nm">{j.id}</div>
                    {#if j.foreign}
                      <!-- Hindsight's drift-sweep lives here too. Naming it stops
                           anyone repurposing it by accident. -->
                      <div class="sub">not Fabric's — leave alone</div>
                    {/if}
                  </td>
                  <td class="sub num">{j.schedule} <span class="sub">{j.timeZone}</span></td>
                  <td>
                    {#if j.state === "PAUSED"}
                      <span class="status status--idle"><Icon name="pause" size={12} /> paused</span>
                    {:else if j.lastOk}
                      <span class="status status--ok"><Icon name="check" size={12} /> enabled</span>
                    {:else}
                      <span class="status status--warn"><Icon name="alert" size={12} /> last run failed</span>
                    {/if}
                  </td>
                  <td class="sub num">{j.lastAttemptTime ? dateZA(j.lastAttemptTime) : "never"}</td>
                  <td class="acts">
                    <button class="lnk" disabled={busy === j.id} onclick={() => runJob(j)}>Run now</button>
                    <button class="lnk" disabled={busy === j.id} onclick={() => togglePause(j)}>
                      {j.state === "PAUSED" ? "Resume" : "Pause"}
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
      {#if sched.data.schedulerServiceAccount}
        <p class="sub foot">
          <Icon name="shield" size={12} /> Jobs authenticate as {sched.data.schedulerServiceAccount} via OIDC.
          Cloud Scheduler has no africa-south1 location, so they run from {sched.data.location} on Johannesburg time.
        </p>
      {/if}
    {/if}
  </section>

  <!-- ── Cost labels ───────────────────────────────────────────────────── -->
  <section class="card">
    <div class="sec">
      <span class="kicker">Cost attribution</span>
      <span class="acts">
        {#if labels.data}<span class="sub">{labels.data.services.length} unlabelled</span>{/if}
        {#if chosenCount > 0}
          <button class="btn-primary" disabled={busy === "labels"} onclick={applyLabels}>
            {busy === "labels" ? "Labelling…" : `Apply ${Math.min(chosenCount, 100)}`}
          </button>
        {/if}
      </span>
    </div>

    {#if labels.loading}
      <p class="sub pad">Loading…</p>
    {:else if labels.error}
      <p class="sub pad"><Icon name="alert" size={12} /> {labels.error}</p>
    {:else if labels.data && labels.data.services.length === 0}
      <p class="sub pad">
        <Icon name="check" size={12} /> Every service carries a portal label, so all spend can be attributed.
      </p>
    {:else if labels.data}
      <p class="sub foot">
        <Icon name="alert" size={12} />
        A service with no <code>portal=</code> label is spend that cannot be attributed to anything — item 16's
        per-portal cost is impossible without these. Drift rule D13 fails while any remain.
      </p>

      {#each projects as p (p)}
        <div class="pj">
          <span class="pj__n">{p}</span>
          <span class="sub">{(labels.data.services.filter((s) => s.project === p)).length} services · set all to</span>
          <select onchange={(e) => fillProject(p, e.currentTarget.value)}>
            <option value="">—</option>
            {#each labels.data.portalIds as id (id)}<option value={id}>{id}</option>{/each}
          </select>
        </div>
      {/each}

      <div class="scroll">
        <table>
          <thead><tr><th>Service</th><th>Project</th><th>Region</th><th>Portal</th></tr></thead>
          <tbody>
            {#each labels.data.services.slice(0, 100) as s (s.project + s.id)}
              <tr>
                <td class="nm">{s.id}</td>
                <td class="sub num">{s.project}</td>
                <td class="sub num">{s.region}</td>
                <td>
                  <select bind:value={choice[`${s.project}/${s.id}`]}>
                    <option value="">— leave unlabelled</option>
                    {#each labels.data.portalIds as id (id)}<option value={id}>{id}</option>{/each}
                  </select>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      {#if labels.data.services.length > 100}
        <!-- No silent caps: say what is not on screen. -->
        <p class="sub foot">
          Showing the first 100 of {labels.data.services.length}. Apply these, then reload for the rest.
        </p>
      {/if}
      {#if labels.data.unreadable.length > 0}
        <p class="sub foot">
          <Icon name="pause" size={12} /> Could not read {labels.data.unreadable.join(", ")} — the runtime service
          account needs <code>roles/run.admin</code> there to label, or <code>roles/run.viewer</code> to list.
        </p>
      {/if}
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .acts { display: flex; align-items: center; gap: 10px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  .nm { font-weight: 700; color: var(--tx); }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  button:disabled { opacity: 0.45; cursor: default; }
  code { font-size: 0.72rem; color: var(--acc); }
  select { background: var(--fill); color: var(--tx); border: 1px solid var(--line);
           border-radius: var(--r-control); padding: 4px 6px; font-size: 0.72rem; }

  .row { display: flex; gap: 9px; padding: 9px 10px; border-radius: var(--r-control);
         box-shadow: inset 0 0 0 1px var(--line); margin-bottom: 6px; align-items: flex-start; }
  .row--warn { border-left: 2px solid var(--warn); }
  .row__t { font-size: 0.7812rem; color: var(--tx); font-weight: 700; }
  .grow { flex: 1; min-width: 0; }
  .grow .sub { overflow-wrap: anywhere; }

  .pj { display: flex; align-items: center; gap: 10px; padding: 6px 2px; }
  .pj__n { font-size: 0.75rem; font-weight: 800; color: var(--tx); min-width: 190px; }
</style>
